import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/** noteKey 本文が「注意: …」形式でも見出しと二重にしない */
function stripLeadingNoteLabel(body: string, noteLabel: string): string {
  const trimmed = body.trim();
  if (!trimmed || !noteLabel.trim()) return trimmed;
  const label = noteLabel.trim();
  for (const sep of [':', '：', ' ·', '·']) {
    const prefix = `${label}${sep}`;
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length).trimStart();
  }
  return trimmed;
}

interface CollapsibleHelpSectionProps {
  /** 翻訳キーで見出しを指定（title を渡す場合は省略可） */
  titleKey?: string;
  /** 翻訳済みの見出し文字列を直接指定（titleKey より優先） */
  title?: string;
  descriptionKey?: string;
  stepsKey?: string[];
  noteKey?: string;
  storageKey: string;
  defaultExpanded?: boolean;
  collapsedButtonKey?: string;
  customContent?: React.ReactNode;
  children?: ReactNode;
  dismissOnEscape?: boolean;
  dismissOnOutsideClick?: boolean;
  dismissOnScroll?: boolean;
  /**
   * 折りたたみ時はアイコン中心（aria-label は維持）。
   * SampleListChromeTools のメニューから `.help-trigger-compact` を叩く用途。
   */
  compact?: boolean;
  /**
   * 展開本文をこの要素へ portal（chrome 内 d-none ドックから見える場所へ出す）
   */
  expandedPortalId?: string;
}

export default function CollapsibleHelpSection({
  titleKey,
  title,
  descriptionKey,
  stepsKey = [],
  noteKey,
  storageKey,
  defaultExpanded = false,
  collapsedButtonKey,
  customContent,
  children,
  dismissOnEscape = true,
  dismissOnOutsideClick = false,
  dismissOnScroll = false,
  compact = false,
  expandedPortalId,
}: CollapsibleHelpSectionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultExpanded === false) {
      setIsExpanded(false);
      const savedState = localStorage.getItem(`help_section_${storageKey}`);
      if (savedState === 'true') {
        localStorage.removeItem(`help_section_${storageKey}`);
      }
    } else {
      const savedState = localStorage.getItem(`help_section_${storageKey}`);
      if (savedState !== null) {
        setIsExpanded(savedState === 'true');
      } else {
        setIsExpanded(defaultExpanded);
      }
    }
  }, [storageKey, defaultExpanded]);

  useEffect(() => {
    if (!expandedPortalId) {
      setPortalTarget(null);
      return;
    }
    setPortalTarget(document.getElementById(expandedPortalId));
  }, [expandedPortalId, isExpanded]);

  const persistExpanded = useCallback(
    (next: boolean) => {
      setIsExpanded(next);
      localStorage.setItem(`help_section_${storageKey}`, String(next));
    },
    [storageKey]
  );

  const handleToggle = () => {
    persistExpanded(!isExpanded);
  };

  useEffect(() => {
    if (
      !isExpanded ||
      (!dismissOnEscape && !dismissOnOutsideClick && !dismissOnScroll)
    ) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (dismissOnEscape && event.key === 'Escape') persistExpanded(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!dismissOnOutsideClick) return;
      const node = rootRef.current;
      const portalNode = portalTarget;
      if (!node && !portalNode) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (node?.contains(target) || portalNode?.contains(target)) return;
      persistExpanded(false);
    };
    let scrollArmed = false;
    const armScroll = window.setTimeout(() => {
      scrollArmed = true;
    }, 120);
    const onScroll = (event: Event) => {
      if (!dismissOnScroll || !scrollArmed) return;
      const node = rootRef.current;
      const target = event.target;
      if (node && target instanceof Node && node.contains(target)) return;
      if (portalTarget && target instanceof Node && portalTarget.contains(target)) {
        return;
      }
      persistExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    if (dismissOnScroll) {
      window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    }
    return () => {
      window.clearTimeout(armScroll);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [
    isExpanded,
    dismissOnEscape,
    dismissOnOutsideClick,
    dismissOnScroll,
    persistExpanded,
    portalTarget,
  ]);

  const collapsedLabel = collapsedButtonKey
    ? t(collapsedButtonKey, { defaultValue: '使い方を表示' })
    : t('common.show_help', { defaultValue: '使い方を表示' });

  const heading =
    title ??
    (titleKey
      ? t(titleKey, { defaultValue: '使い方' })
      : t('common.show_help', { defaultValue: '使い方' }));

  const noteLabel = t('common.note', { defaultValue: '注意' });
  const noteBody = noteKey
    ? stripLeadingNoteLabel(t(noteKey, { defaultValue: '' }), noteLabel)
    : '';

  const body = customContent ? (
    customContent
  ) : children ? (
    children
  ) : (
    <>
      {descriptionKey && (
        <p className="mb-3 mt-2">
          {t(descriptionKey, { defaultValue: 'このページの使い方を説明します。' })}
        </p>
      )}
      {stepsKey.length > 0 && (
        <ol className="mb-3 ps-3" style={{ lineHeight: '1.8' }}>
          {stepsKey.map((stepKey, index) => (
            <li key={index} className="mb-1">
              {t(stepKey, { defaultValue: `ステップ${index + 1}` })}
            </li>
          ))}
        </ol>
      )}
      {noteBody ? (
        <div className="alert alert-warning mb-0 mt-3" style={{ padding: '0.75rem' }}>
          <i className="fas fa-exclamation-triangle me-2" aria-hidden="true" />
          <strong>{noteLabel}</strong>
          {' · '}
          {noteBody}
        </div>
      ) : null}
    </>
  );

  const expandedPanel = (
    <Alert variant="info" className="mb-3 help-section fade-in">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <Alert.Heading className="d-flex align-items-center mb-0 flex-grow-1">
          <i className="fas fa-question-circle me-2" aria-hidden="true" />
          {heading}
        </Alert.Heading>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleToggle}
          className="ms-2"
          title={t('common.close', { defaultValue: '閉じる' })}
          aria-label={t('common.close', { defaultValue: '閉じる' })}
        >
          <i className="fas fa-times me-1" aria-hidden="true" />
          {t('common.close', { defaultValue: '閉じる' })}
        </Button>
      </div>
      <div>{body}</div>
    </Alert>
  );

  // compact（アイコンのみ）: aria は短語、title は「ヘルプを表示」等の長語
  const helpAria = compact
    ? t('common.help', { defaultValue: 'ヘルプ' })
    : collapsedLabel;

  if (!isExpanded) {
    return (
      <div className="mb-0" ref={rootRef}>
        <Button
          variant="link"
          size="sm"
          onClick={handleToggle}
          className={`px-0 py-0 text-decoration-none${compact ? ' help-trigger-compact' : ''}`}
          aria-label={helpAria}
          title={collapsedLabel}
        >
          <i
            className={`fas fa-question-circle${compact ? '' : ' me-1'}`}
            aria-hidden="true"
          />
          {/* compact は aria-label のみ。visually-hidden に長語を置くと a11y 名が「ヘルプを表示」になる */}
          {compact ? null : (
            <span className="help-collapsed-label">{collapsedLabel}</span>
          )}
        </Button>
      </div>
    );
  }

  if (expandedPortalId && portalTarget) {
    return (
      <div className="mb-0" ref={rootRef}>
        {/* トリガーはドック内に残し、本文だけ portal */}
        <Button
          variant="link"
          size="sm"
          onClick={handleToggle}
          className={`px-0 py-0 text-decoration-none${compact ? ' help-trigger-compact' : ''}`}
          aria-label={t('common.close', { defaultValue: '閉じる' })}
          title={t('common.close', { defaultValue: '閉じる' })}
        >
          <i className="fas fa-times-circle" aria-hidden="true" />
          <span className="visually-hidden">{t('common.close', { defaultValue: '閉じる' })}</span>
        </Button>
        {createPortal(expandedPanel, portalTarget)}
      </div>
    );
  }

  return <div ref={rootRef}>{expandedPanel}</div>;
}
